import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { GTColumnSelectAnchor } from 'src/components/GenericTable';
import { formatDate } from 'src/utils/misc';
import { TableIds } from 'src/api/types/misc';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { EventCompletionStatus, EventsQuery } from 'src/graphql/_generated';
import { DataValue, TableUserItem } from 'src/common';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { filter } from 'lodash';
import { ColorSet } from 'src/redux/reducers/theme';
import { CompletionStatus } from './comps';
import { TLLink } from 'src/pages/DashboardPage/comps';
import { IconEdit } from '../CommonMenu/icons';
import styled from 'styled-components';

const TDMDiv = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  padding: 4px 0;
  padding-left: 6px;

  cursor: pointer;
  &:hover {
    svg,
    path {
      fill: ${({ theme }) => theme.colors.gp1} !important;
    }
  }
`;

type Event = EventsQuery['events'][number];

type UseColumns = (a: {
  data?: EventsQuery['events'];
  objectId: string;
  setEventId: (eventId: string | null) => void;
  hasVersion: boolean;
}) => {
  columns: ColumnDef<Event>[];
  GTColumnSelectAnchorExported: JSX.Element;
};

const defaultColumns = ['user', 'title', 'due', 'completionStatus'];

export const useEventsColumns: UseColumns = ({ data = [], objectId, setEventId, hasVersion }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(`${TableIds.events}-columns`, defaultColumns);

  const columns = useMemo(() => {
    const internalIds = [GTInternalIds.tdm];

    const list: ColumnDef<Event>[] = [
      {
        id: 'user',
        header: 'Owner',
        accessorFn: (row) => row.assignedTo?.text || row.assignedTo?.email,
        cell: ({ row }) => {
          const assignedTo = row.original.assignedTo;
          if (!assignedTo) return null;
          return (
            <TableUserItem
              name={assignedTo.text || assignedTo.email}
              profilePhotoPath={assignedTo.profilePhotoPath}
              interactive={{
                email: assignedTo.email,
                scope: 'workspace',
              }}
            />
          );
        },
        sortingFn: sortingFns.text,
      },
      {
        id: 'title',
        header: 'Title',
        accessorFn: (row) => row.notes,
        cell: ({ row }) => <DataValue>{row.original.notes}</DataValue>,
        sortingFn: sortingFns.text,
      },
      {
        id: 'completionStatus',
        header: 'Completion Status',
        accessorFn: (row) => row.completionStatus,
        cell: ({ row }) => (
          <CompletionStatus
            status={row.original.completionStatus}
            objectId={objectId}
            eventId={row.original.id}
            objectType={row.original.objectType}
            hasVersion={hasVersion}
          />
        ),
        sortingFn: sortingFns.text,
      },
      {
        id: 'due',
        header: 'Due Date',
        accessorFn: (row) => row.dueDate,
        cell: ({ row }) => {
          const date = row.original.dueDate;
          if (!date) return null;

          let color: keyof ColorSet = 'prussianBlue';
          if (row.original.completionStatus === EventCompletionStatus.Done) {
            color = 'cflowerBlue';
          } else if (row.original.isOverdue) {
            color = 'statusRed';
          }

          return (
            <TLLink colorOverride={color} date={date}>
              {formatDate(date)}
            </TLLink>
          );
        },
        sortingFn: sortingFns.datetime,
      },
      {
        id: 'marked_as_completed',
        header: 'Date Completed',
        accessorFn: (row) => row.completedAt,
        cell: ({ row }) => {
          const date = row.original.completedAt;
          if (!date) return null;
          return <TLLink date={date}>{formatDate(date)}</TLLink>;
        },
        sortingFn: sortingFns.datetime,
      },
      {
        id: 'created',
        header: 'Date Created',
        accessorFn: (row) => row.meta?.createdAt,
        cell: ({ row }) => {
          const date = row.original.meta?.createdAt;
          if (!date) return null;
          return <TLLink date={date}>{formatDate(date, { formatType: 'datetime' })}</TLLink>;
        },
        sortingFn: sortingFns.datetime,
      },

      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: ({ row }) => {
          const onEditClick: (() => void) | undefined = () => {
            setEventId(row.original.id);
          };

          if (hasVersion) {
            return null;
          }

          return (
            <div css="display: flex; grid-gap: 8px;">
              <TDMDiv className="ThreeDots__Wraper" onClick={onEditClick} data-cy="edit-action-btn">
                <IconEdit />
              </TDMDiv>
            </div>
          );
        },
        size: 1,
        maxSize: 50,
        enableSorting: false,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns, objectId, hasVersion]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={TableIds.events}
      options={[
        { key: 'user', title: 'Owner' },
        // { key: 'name', title: 'Name' },
        { key: 'title', title: 'Title' },
        { key: 'completionStatus', title: 'Completion Status' },
        { key: 'due', title: 'Due Date' },
        { key: 'marked_as_completed', title: 'Date Completed' },
        { key: 'created', title: 'Date Created' },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return {
    columns,
    GTColumnSelectAnchorExported,
  };
};
